import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import './DeviceHealthChart.css';

const DeviceHealthChart = ({ healthLogs, deviceId }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!healthLogs || healthLogs.length === 0) return;

    // Convert ISO timestamps to local Date objects for proper timezone display
    const timestamps = healthLogs.map(log => new Date(log.timestamp));
    
    // Extract data from logs
    const temperatures = healthLogs.map(log => log.deviceHealth?.temperatureCelsius || null);
    const batteryLevels = healthLogs.map(log => log.deviceHealth?.batteryLevel || null);
    const chargingPowers = healthLogs.map(log => (log.deviceHealth?.chargingPower || 0) / 1000); // Convert to W
    const freeStorages = healthLogs.map(log => (log.deviceHealth?.freeStorage || 0) / 1e9); // Convert to GB

    // Create the subplot figure
    const data = [
      // Temperature trace
      {
        x: timestamps,
        y: temperatures,
        type: 'scatter',
        mode: 'lines',
        line: { color: 'orange', width: 2 },
        name: 'Temp (°C)',
        hovertemplate: '<b>Time:</b> %{x}<br><b>Temp:</b> %{y} °C<extra></extra>',
        xaxis: 'x',
        yaxis: 'y'
      },
      // Battery level trace
      {
        x: timestamps,
        y: batteryLevels,
        type: 'scatter',
        mode: 'lines',
        line: { color: '#fc5f21', width: 2 },
        name: 'Battery (%)',
        hovertemplate: '<b>Time:</b> %{x}<br><b>Battery:</b> %{y}%<extra></extra>',
        xaxis: 'x2',
        yaxis: 'y2'
      },
      // Charging power trace
      {
        x: timestamps,
        y: chargingPowers,
        type: 'scatter',
        mode: 'lines',
        line: { color: 'cyan', width: 2 },
        name: 'Charging Power (W)',
        hovertemplate: '<b>Time:</b> %{x}<br><b>Charging Power:</b> %{y:.2f} W<extra></extra>',
        xaxis: 'x3',
        yaxis: 'y3'
      },
      // Free storage trace
      {
        x: timestamps,
        y: freeStorages,
        type: 'scatter',
        mode: 'lines',
        line: { color: 'rgba(255, 100, 0, .95)', width: 2 },
        name: 'Free Storage (GB)',
        hovertemplate: '<b>Time:</b> %{x}<br><b>Free Storage:</b> %{y:.2f} GB<extra></extra>',
        xaxis: 'x4',
        yaxis: 'y4'
      }
    ];

    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const axisColor = isDark ? '#ccc' : '#444';
    const gridColor = isDark ? '#444' : '#e1e4e8';
    const bgColor = isDark ? '#262a2e' : '#ffffff';
    const paperColor = isDark ? '#262a2e' : '#f6f8fa';

    const layout = {
      title: {
        text: `<b>${deviceId} Device Health History</b>`,
        x: 0.5,
        xanchor: 'center'
      },
      grid: {
        rows: 4,
        columns: 1,
        pattern: 'independent',
        roworder: 'top to bottom'
      },
      xaxis: {
        title: '',
        showline: true,
        linecolor: axisColor,
        tickfont: { color: axisColor },
        domain: [0, 1],
        anchor: 'y'
      },
      yaxis: {
        title: { text: '<b>Temp (°C)</b>', font: { color: axisColor } },
        range: [20, 55],
        showgrid: true,
        gridcolor: gridColor,
        tickfont: { color: axisColor },
        domain: [0.775, 1]
      },
      xaxis2: {
        title: '',
        showline: true,
        linecolor: axisColor,
        tickfont: { color: axisColor },
        domain: [0, 1],
        anchor: 'y2',
        matches: 'x'
      },
      yaxis2: {
        title: { text: '<b>Battery (%)</b>', font: { color: axisColor } },
        range: [0, 100],
        showgrid: true,
        gridcolor: gridColor,
        tickfont: { color: axisColor },
        domain: [0.525, 0.725]
      },
      xaxis3: {
        title: '',
        showline: true,
        linecolor: axisColor,
        tickfont: { color: axisColor },
        domain: [0, 1],
        anchor: 'y3',
        matches: 'x'
      },
      yaxis3: {
        title: { text: '<b>Charging Power (W)</b>', font: { color: axisColor } },
        showgrid: true,
        gridcolor: gridColor,
        tickfont: { color: axisColor },
        domain: [0.275, 0.475]
      },
      xaxis4: {
        title: { text: '<b>Timestamp</b>', font: { color: axisColor } },
        showline: true,
        linecolor: axisColor,
        tickfont: { color: axisColor },
        domain: [0, 1],
        anchor: 'y4',
        matches: 'x'
      },
      yaxis4: {
        title: { text: '<b>Free Storage (GB)</b>', font: { color: axisColor } },
        range: [0, 110],
        showgrid: true,
        gridcolor: gridColor,
        tickfont: { color: axisColor },
        domain: [0, 0.225]
      },
      template: isDark ? 'plotly_dark' : 'plotly_white',
      font: { family: 'Arial, sans-serif', color: axisColor },
      plot_bgcolor: bgColor,
      paper_bgcolor: paperColor,
      hovermode: 'x unified',
      showlegend: false,
      autosize: true,
      margin: { l: 70, r: 30, t: 60, b: 90 }
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d']
    };

    const chartElement = chartRef.current;
    Plotly.newPlot(chartElement, data, layout, config);

    // Cleanup
    return () => {
      if (chartElement) {
        Plotly.purge(chartElement);
      }
    };
  }, [healthLogs, deviceId]);

  if (!healthLogs || healthLogs.length === 0) {
    return (
      <div className="device-health-chart">
        <div className="empty-state">
          <p>No health data available for this device.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="device-health-chart">
      <div ref={chartRef} />
    </div>
  );
};

export default DeviceHealthChart;

